import React, { useState, useEffect } from 'react';
import {
  transactionsApi,
  InventoryPurchaseTransactionRequest,
  TransactionItem,
  PaymentMethod,
  ItemType,
  formatCurrency,
  calculateTransactionTotal,
  getItemTypeDisplay,
} from '../../api/transactions';
import { suppliersApi } from '../../api/suppliers';
import { suppliesApi } from '../../api/supplies';
import { equipmentApi } from '../../api/equipment';
import { feedStockApi } from '../../api/feedStock';
import AmountInput from '../ui/AmountInput';

interface Supplier {
  id: string;
  name: string;
  phone?: string;
  address?: string;
  email?: string;
}

interface Supply {
  id: string;
  name: string;
  category: string;
  unit: string;
}

interface Equipment {
  id: string;
  name: string;
  type: string;
  model?: string;
}

interface FeedStock {
  id: string;
  feedType: string;
  brand: string;
  weightKg: number;
}

interface InventoryPurchaseTransactionFormProps {
  onSuccess?: (transactionId: string) => void;
  onCancel?: () => void;
}

export const InventoryPurchaseTransactionForm: React.FC<InventoryPurchaseTransactionFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    supplierName: '',
    supplierPhone: '',
    supplierAddress: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    paymentMethod: PaymentMethod.CASH,
    notes: '',
  });

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [feedStock, setFeedStock] = useState<FeedStock[]>([]);
  const [items, setItems] = useState<TransactionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [suppliersResponse, suppliesResponse, equipmentResponse, feedStockResponse] = await Promise.all([
        suppliersApi.getSuppliers(),
        suppliesApi.getSupplies(),
        equipmentApi.getEquipment(),
        feedStockApi.getFeedStocks(),
      ]);
      
      // Extract data from API responses using type assertions to handle different response formats
      const suppliers = Array.isArray(suppliersResponse) 
        ? suppliersResponse 
        : (suppliersResponse as any)?.data || [];
        
      const supplies = Array.isArray(suppliesResponse) 
        ? suppliesResponse 
        : (suppliesResponse as any)?.data || [];
        
      // Handle equipment response which might have nested data structure
      let equipment: any[] = [];
      if (Array.isArray(equipmentResponse)) {
        equipment = equipmentResponse;
      } else if (equipmentResponse && typeof equipmentResponse === 'object') {
        const eqData = (equipmentResponse as any).data;
        if (Array.isArray(eqData)) {
          equipment = eqData;
        } else if (eqData && Array.isArray(eqData.data)) {
          equipment = eqData.data;
        }
      }
      
      const feedStock = Array.isArray(feedStockResponse) 
        ? feedStockResponse 
        : (feedStockResponse as any)?.data || [];
      
      setSuppliers(suppliers as Supplier[]);
      setSupplies(supplies as Supply[]);
      setEquipment(equipment as Equipment[]);
      setFeedStock(feedStock as FeedStock[]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSupplierSelect = (supplierId: string) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        supplierName: supplier.name,
        supplierPhone: supplier.phone || '',
        supplierAddress: supplier.address || '',
      }));
    }
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      itemType: ItemType.OTHER,
      itemReferenceId: '',
      productName: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
      notes: '',
    }]);
  };

  const updateItem = (index: number, field: keyof TransactionItem, value: any) => {
    setItems(prev => prev.map((item, i) => {
      if (i === index) {
        const updatedItem = { ...item, [field]: value };
        
        // Auto-calculate total price when quantity or unit price changes
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.totalPrice = updatedItem.quantity * updatedItem.unitPrice;
        }
        
        // Auto-populate product name when reference item is selected
        if (field === 'itemReferenceId' && value) {
          const itemType = updatedItem.itemType;
          let itemName = '';
          
          switch (itemType) {
            case ItemType.SUPPLY:
              const supply = supplies.find(s => s.id === value);
              itemName = supply ? `${supply.name} (${supply.category})` : '';
              break;
            case ItemType.EQUIPMENT:
              const eq = equipment.find(e => e.id === value);
              itemName = eq ? `${eq.name} - ${eq.type}${eq.model ? ` - ${eq.model}` : ''}` : '';
              break;
            case ItemType.FEED:
              const feed = feedStock.find(f => f.id === value);
              itemName = feed ? `${feed.feedType} - ${feed.brand} (${feed.weightKg}kg)` : '';
              break;
          }
          
          if (itemName) {
            updatedItem.productName = itemName;
          }
        }
        
        return updatedItem;
      }
      return item;
    }));
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const getAvailableItemsForType = (itemType: ItemType) => {
    switch (itemType) {
      case ItemType.SUPPLY:
        return supplies;
      case ItemType.EQUIPMENT:
        return equipment;
      case ItemType.FEED:
        return feedStock;
      default:
        return [];
    }
  };

  const getItemDisplayName = (itemType: ItemType, itemId: string) => {
    switch (itemType) {
      case ItemType.SUPPLY:
        const supply = supplies.find(s => s.id === itemId);
        return supply ? `${supply.name} (${supply.category})` : 'Unknown Supply';
      case ItemType.EQUIPMENT:
        const eq = equipment.find(e => e.id === itemId);
        return eq ? `${eq.name} - ${eq.type}` : 'Unknown Equipment';
      case ItemType.FEED:
        const feed = feedStock.find(f => f.id === itemId);
        return feed ? `${feed.feedType} - ${feed.brand}` : 'Unknown Feed';
      default:
        return 'Select an item';
    }
  };

  const calculateTotal = () => {
    return calculateTransactionTotal(items);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    const validationErrors: string[] = [];
    if (!formData.supplierName.trim()) validationErrors.push('Supplier name is required');
    if (!formData.purchaseDate) validationErrors.push('Purchase date is required');
    if (items.length === 0) validationErrors.push('At least one item is required');
    
    // Validate items
    items.forEach((item, index) => {
      if (!item.itemReferenceId) validationErrors.push(`Item ${index + 1}: Please select an item`);
      if (!item.quantity || item.quantity <= 0) validationErrors.push(`Item ${index + 1}: Quantity must be greater than 0`);
      if (!item.unitPrice || item.unitPrice <= 0) validationErrors.push(`Item ${index + 1}: Unit price must be greater than 0`);
    });

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    setErrors([]);

    try {
      // Convert TransactionItem to API expected format
      const apiItems = items.map(item => ({
        type: item.itemType === ItemType.SUPPLY ? 'SUPPLY' as const :
              item.itemType === ItemType.EQUIPMENT ? 'EQUIPMENT' as const :
              'FEED' as const,
        itemId: item.itemReferenceId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      }));

      const transactionData: InventoryPurchaseTransactionRequest = {
        supplierName: formData.supplierName,
        supplierPhone: formData.supplierPhone,
        supplierAddress: formData.supplierAddress,
        purchaseDate: new Date(formData.purchaseDate),
        paymentMethod: formData.paymentMethod,
        items: apiItems,
        notes: formData.notes,
      };
      const result = await transactionsApi.createInventoryPurchaseTransaction(transactionData);
      if (onSuccess) {
        onSuccess(result.id);
      }
    } catch (error: any) {
      console.error('Error creating inventory purchase transaction:', error);
      setErrors([error.message || 'Failed to create inventory purchase transaction']);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Inventory Purchase Transaction</h2>

      {errors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h3>
          <ul className="list-disc pl-5 text-sm text-red-700">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Supplier Information */}
        <div className="bg-slate-50/80 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Supplier Information</h3>
          
          <div className="mb-4">
            <label htmlFor="supplierSelect" className="block text-sm font-medium text-slate-700 mb-1">
              Select Existing Supplier (Optional)
            </label>
            <select
              id="supplierSelect"
              onChange={(e) => handleSupplierSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option key="empty-option-1753461297346" value="">Select a supplier or enter manually below</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name} {supplier.phone && `- ${supplier.phone}`}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="supplierName" className="block text-sm font-medium text-slate-700 mb-1">
                Supplier Name *
              </label>
              <input
                type="text"
                id="supplierName"
                name="supplierName"
                value={formData.supplierName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label htmlFor="supplierPhone" className="block text-sm font-medium text-slate-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                id="supplierPhone"
                name="supplierPhone"
                value={formData.supplierPhone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="supplierAddress" className="block text-sm font-medium text-slate-700 mb-1">
                Address
              </label>
              <textarea
                id="supplierAddress"
                name="supplierAddress"
                value={formData.supplierAddress}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Purchase Details */}
        <div className="bg-slate-50/80 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Purchase Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="purchaseDate" className="block text-sm font-medium text-slate-700 mb-1">
                Purchase Date *
              </label>
              <input
                type="date"
                id="purchaseDate"
                name="purchaseDate"
                value={formData.purchaseDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label htmlFor="paymentMethod" className="block text-sm font-medium text-slate-700 mb-1">
                Payment Method
              </label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.values(PaymentMethod).map(method => (
                  <option key={method} value={method}>{method.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-slate-50/80 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-900">Items</h3>
            <button
              type="button"
              onClick={addItem}
              className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700 transition-colors"
            >
              Add Item
            </button>
          </div>
          
          {items.map((item, index) => (
            <div key={index} className="mb-6 p-4 bg-white rounded-md border">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Item Type *
                  </label>
                  <select
                    value={item.itemType}
                    onChange={(e) => updateItem(index, 'itemType', e.target.value as ItemType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {Object.values(ItemType).map(type => (
                      <option key={type} value={type}>{getItemTypeDisplay(type)}</option>
                    ))}
                  </select>
                </div>
                
                {(item.itemType === ItemType.SUPPLY || item.itemType === ItemType.EQUIPMENT || item.itemType === ItemType.FEED) && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Select Item
                    </label>
                    <select
                      value={item.itemReferenceId || ''}
                      onChange={(e) => updateItem(index, 'itemReferenceId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option key="empty-option-1753461297346" value="">Select an item</option>
                      {getAvailableItemsForType(item.itemType).map((availableItem: any) => (
                        <option key={availableItem.id} value={availableItem.id}>
                          {getItemDisplayName(item.itemType, availableItem.id)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={item.productName}
                    onChange={(e) => updateItem(index, 'productName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Unit Price *
                  </label>
                  <AmountInput
                    value={item.unitPrice}
                    onChange={(v) => updateItem(index, 'unitPrice', v ?? 0)}
                    inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min={0}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Total Price
                  </label>
                  <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-slate-700">
                    {formatCurrency(item.totalPrice)}
                  </div>
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="md:col-span-5">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Notes
                  </label>
                  <input
                    type="text"
                    value={item.notes || ''}
                    onChange={(e) => updateItem(index, 'notes', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Additional notes for this item..."
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {items.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              No items added yet. Click "Add Item" to get started.
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-1">
            Transaction Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Additional notes about this purchase..."
          />
        </div>

        {/* Total */}
        <div className="bg-cyan-50 p-4 rounded-lg">
          <div className="text-right">
            <span className="text-2xl font-bold text-cyan-800">
              Total: {formatCurrency(calculateTotal())}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 rounded-md text-slate-700 hover:bg-slate-50/80 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading || items.length === 0}
            className="px-6 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Processing...' : 'Create Purchase Transaction'}
          </button>
        </div>
      </form>
    </div>
  );
}; 